import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Button,
  FieldError,
  Fieldset,
  Form,
  Label,
  ListBox,
  Select,
  toast,
} from "@heroui/react";
import { EmptyState, ErrorState, SkeletonForm } from "@branding/ui";
import useSWR from "swr";
import { NoIndex } from "../components/NoIndex";
import {
  createOrder,
  fetchAddresses,
  fetchCart,
  loginUrl,
} from "../lib/api";
import { formatIdr } from "../lib/format";
import type { BuyerAddress, Cart } from "../lib/types";
import { ApiClientError, fieldErrorMap } from "../lib/v2";

const RETURN_PATH = "/pasar/checkout";

export function CheckoutPage() {
  const cart = useSWR<Cart>("cart", fetchCart);
  const addresses = useSWR<BuyerAddress[]>("addresses", fetchAddresses);
  const [addressId, setAddressId] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!addresses.error) return;
    if (
      addresses.error instanceof ApiClientError &&
      addresses.error.unauthorized
    ) {
      window.location.replace(loginUrl(RETURN_PATH));
    }
  }, [addresses.error]);

  useEffect(() => {
    if (!addressId && addresses.data?.length) {
      const preferred =
        addresses.data.find((row) => row.isDefault) ?? addresses.data[0];
      if (preferred) setAddressId(preferred.id);
    }
  }, [addressId, addresses.data]);

  const lines = cart.data?.lines ?? [];
  const merchants = new Set(lines.map((line) => line.merchant_id));
  const selected = addresses.data?.find((row) => row.id === addressId);
  const subtotal = lines.reduce((sum, line) => sum + line.price * line.qty, 0);
  const addressItems = useMemo(() => addresses.data ?? [], [addresses.data]);

  if (
    addresses.error instanceof ApiClientError &&
    addresses.error.unauthorized
  ) {
    return (
      <main className="mx-auto max-w-xl px-4 py-16">
        <NoIndex />
        <p>Mengalihkan ke halaman masuk…</p>
      </main>
    );
  }

  if (cart.error || (addresses.error && !addresses.error.unauthorized)) {
    return (
      <main className="mx-auto max-w-xl px-4 py-12 md:px-6">
        <NoIndex />
        <ErrorState
          title="Checkout tidak bisa dibuka"
          description={
            cart.error instanceof ApiClientError
              ? cart.error.traceId
              : undefined
          }
          onRetry={() => {
            void cart.mutate();
            void addresses.mutate();
          }}
        />
      </main>
    );
  }

  if ((cart.isLoading && !cart.data) || (addresses.isLoading && !addresses.data)) {
    return (
      <main className="mx-auto max-w-xl px-4 py-12 md:px-6">
        <NoIndex />
        <SkeletonForm />
      </main>
    );
  }

  if (lines.length === 0) {
    return (
      <main className="mx-auto max-w-xl px-4 py-12 md:px-6">
        <NoIndex />
        <EmptyState
          title="Keranjang masih kosong"
          description="Isi keranjang sebelum checkout."
          action={
            <Button variant="primary" onPress={() => window.location.assign("/pasar")}>
              Jelajahi pasar
            </Button>
          }
        />
      </main>
    );
  }

  if (!addressItems.length) {
    return (
      <main className="mx-auto max-w-xl px-4 py-12 md:px-6">
        <NoIndex />
        <EmptyState
          title="Belum ada alamat kirim"
          description="Tambah alamat di akun sebelum membuat pesanan."
          action={
            <Button variant="primary" onPress={() => window.location.assign("/akun/alamat")}>
              Tambah alamat
            </Button>
          }
        />
      </main>
    );
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (merchants.size > 1) {
      toast.danger("Satu toko per pesanan. Hapus baris dari toko lain.");
      return;
    }
    if (!addressId) {
      setFields({ address_id: "Pilih alamat pengiriman." });
      return;
    }
    setSubmitting(true);
    setFields({});
    try {
      const order = await createOrder({
        addressId,
        shippingZoneId: selected?.shippingZoneId,
      });
      toast.success(`Pesanan ${order.code} dibuat`);
      if (order.payment?.dummy_pay_url) {
        window.location.assign(order.payment.dummy_pay_url);
        return;
      }
      window.location.assign(`/akun/pesanan/${order.id}`);
    } catch (caught) {
      const clientError = caught instanceof ApiClientError ? caught : null;
      setFields(fieldErrorMap(clientError?.errors ?? []));
      toast.danger(
        clientError?.title ?? "Pesanan gagal dibuat",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-xl px-4 py-10 md:px-6">
      <NoIndex />
      <h1 className="text-3xl font-semibold">Checkout</h1>
      {merchants.size > 1 ? (
        <p className="mt-3 text-sm text-danger" role="alert">
          Keranjang berisi lebih dari satu toko. Checkout ditolak sampai tersisa
          satu toko.
        </p>
      ) : null}
      <ul className="mt-6 flex flex-col gap-2 text-sm">
        {lines.map((line) => (
          <li key={line.sku_id} className="flex justify-between gap-4">
            <span>
              {line.product_name} · {line.sku_code} × {line.qty}
            </span>
            <span className="pasar-price">{formatIdr(line.price * line.qty)}</span>
          </li>
        ))}
      </ul>
      <p className="pasar-price mt-4 text-right text-lg font-semibold">
        {formatIdr(subtotal)}
      </p>
      <Form className="mt-8" onSubmit={onSubmit}>
        <Fieldset>
          <Fieldset.Legend>Pengiriman</Fieldset.Legend>
          <Fieldset.Group>
            <Select
              selectedKey={addressId}
              onSelectionChange={(key) => {
                if (typeof key === "string") setAddressId(key);
              }}
              isRequired
              name="address_id"
              isInvalid={Boolean(fields.address_id)}
              fullWidth
            >
              <Label>Alamat</Label>
              <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {addressItems.map((address) => (
                    <ListBox.Item
                      key={address.id}
                      id={address.id}
                      textValue={`${address.label} ${address.line1}`}
                    >
                      {address.label} · {address.recipient} · {address.line1}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
              {fields.address_id ? (
                <FieldError>{fields.address_id}</FieldError>
              ) : null}
            </Select>
          </Fieldset.Group>
          <Fieldset.Actions>
            <Button
              type="submit"
              variant="primary"
              isDisabled={submitting || merchants.size > 1}
            >
              {submitting ? "Memproses…" : "Buat pesanan"}
            </Button>
          </Fieldset.Actions>
        </Fieldset>
      </Form>
    </main>
  );
}
